import { useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import AuthHeader from '../components/AuthHeader'

function parseHashParams() {
  const hash = window.location.hash?.replace(/^#/, '') || ''
  return new URLSearchParams(hash)
}

export default function PasswordRecovery({ onBackToSignIn }) {
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [ready, setReady] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = parseHashParams()
        const hashError = params.get('error_description') || params.get('error')
        if (hashError) throw new Error(decodeURIComponent(hashError))

        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')

        const { data: { session } } = await supabase.auth.getSession()
        if (!session && accessToken && refreshToken) {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (setSessionError) throw setSessionError
        }

        const { data: { session: latestSession } } = await supabase.auth.getSession()
        if (!latestSession) throw new Error('Recovery link is invalid or expired. Please request a new reset link.')
        setReady(true)
      } catch (err) {
        setError(err?.message || 'Unable to verify recovery link.')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setMessage('')
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setUpdating(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    setUpdating(false)
    if (updateError) {
      setError(updateError.message || 'Could not update password.')
      return
    }
    setMessage('Password updated successfully. You can now sign in.')
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-full">
        <AuthHeader showBack={false} />
        <div className="flex items-center justify-center bg-neutral-50 px-4 pt-28 min-h-screen">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin text-brand mx-auto" />
            <p className="mt-4 text-sm text-muted">Verifying your recovery link...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state - link invalid
  if (error && !ready) {
    return (
      <div className="min-h-full">
        <AuthHeader showBack={true} onBack={onBackToSignIn} />
        <div className="flex items-center justify-center bg-neutral-50 px-4 pt-28 min-h-screen">
          <div className="w-full max-w-[440px]">
            <div className="bg-surface border border-neutral-200 p-7 text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-rose-600" />
              </div>
              <h2 className="text-lg font-bold text-ink mb-2">Invalid Recovery Link</h2>
              <p className="text-sm text-muted mb-6">{error}</p>
              <button onClick={onBackToSignIn} className="btn-primary w-full">
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main form
  return (
    <div className="min-h-full">
      {/* ✅ AuthHeader - fixed at top */}
      <AuthHeader showBack={true} onBack={onBackToSignIn} />

      {/* Main Content - with padding for header */}
      <div className="flex items-center justify-center bg-neutral-50 px-4 py-12 pt-28">
        <div className="w-full max-w-[440px]">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 bg-brand flex items-center justify-center mb-4">
              <KeyRound className="text-white" size={26} />
            </div>
            <h1 className="text-[26px] font-bold text-ink">Reset Password</h1>
            <p className="text-sm text-muted mt-1">Enter your new password below.</p>
          </div>

          <div className="bg-surface border border-neutral-200 p-7">
            {error && (
              <div className="flex items-center gap-2 text-xs text-rose-600 border border-rose-200 bg-rose-50 px-3 py-2 mb-4">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {message && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 border border-emerald-200 bg-emerald-50 px-3 py-2 mb-4 animate-fade-in">
                <CheckCircle2 size={14} /> {message}
              </div>
            )}

            {message ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-emerald-600" />
                </div>
                <p className="text-sm text-ink font-medium mb-6">{message}</p>
                <button onClick={onBackToSignIn} className="btn-primary w-full">
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label className="label-above">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-flat pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-ink"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted mt-1.5">Must be at least 8 characters</p>
                </div>

                <div>
                  <label className="label-above">Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-flat"
                    autoComplete="new-password"
                  />
                  {confirmPassword && password && password !== confirmPassword && (
                    <p className="text-[11px] text-rose-600 mt-1.5">Passwords do not match</p>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={updating}
                >
                  {updating ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" /> Updating...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <button
                  type="button"
                  onClick={onBackToSignIn}
                  className="link-secondary w-full text-center text-sm"
                >
                  ← Back to Sign In
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}